/**
 * Standardized hook for debounced search functionality
 * Prevents common anti-patterns and ensures consistent behavior
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDebounce } from "../ui/use-debounce";

export interface DebouncedSearchConfig {
  /** Debounce delay in milliseconds (default: 300) */
  delay?: number;
  /** Whether to clear search on component unmount */
  clearOnUnmount?: boolean;
  /** Initial search value */
  initialValue?: string;
}

export interface DebouncedSearchState {
  /** Current search value (immediate updates for UI) */
  searchValue: string;
  /** Debounced search value (for API calls) */
  debouncedSearchValue: string;
  /** Whether the search is currently being debounced */
  isDebouncing: boolean;
  /** Set the search value */
  setSearchValue: (value: string) => void;
  /** Clear the search value */
  clearSearch: () => void;
}

/**
 * Standardized hook for debounced search functionality
 *
 * @example
 * ```tsx
 * const { searchValue, debouncedSearchValue, setSearchValue } = useDebouncedSearch({
 *   delay: 300,
 *   initialValue: ""
 * });
 *
 * // Use searchValue for immediate UI updates
 * <Input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
 *
 * // Use debouncedSearchValue for API calls
 * useEffect(() => {
 *   if (debouncedSearchValue) {
 *     fetchSearchResults(debouncedSearchValue);
 *   }
 * }, [debouncedSearchValue]);
 * ```
 */
export function useDebouncedSearch(config: DebouncedSearchConfig = {}): DebouncedSearchState {
  const { delay = 300, clearOnUnmount = false, initialValue = "" } = config;

  const [searchValue, setSearchValue] = useState(initialValue);
  const debouncedSearchValue = useDebounce(searchValue, delay);
  const [isDebouncing, setIsDebouncing] = useState(false);

  // Track if search value is different from debounced value
  const previousSearchValue = useRef(searchValue);

  useEffect(() => {
    const isCurrentlyDebouncing = searchValue !== debouncedSearchValue;
    setIsDebouncing(isCurrentlyDebouncing);

    // Update previous value
    previousSearchValue.current = searchValue;
  }, [searchValue, debouncedSearchValue]);

  const clearSearch = useCallback(() => {
    setSearchValue("");
  }, []);

  // Clear search on unmount if configured
  useEffect(() => {
    if (clearOnUnmount) {
      return () => {
        setSearchValue("");
      };
    }
  }, [clearOnUnmount]);

  return {
    searchValue,
    debouncedSearchValue,
    isDebouncing,
    setSearchValue,
    clearSearch,
  };
}

/**
 * Hook for multiple debounced search fields
 * Useful for components with multiple search inputs
 */
export interface MultiDebouncedSearchConfig {
  /** Debounce delay in milliseconds (default: 300) */
  delay?: number;
  /** Initial values for each search field */
  initialValues?: Record<string, string>;
  /** Fields that should be debounced */
  fields: string[];
}

export interface MultiDebouncedSearchState {
  /** Current search values (immediate updates for UI) */
  searchValues: Record<string, string>;
  /** Debounced search values (for API calls) */
  debouncedSearchValues: Record<string, string>;
  /** Whether any field is currently being debounced */
  isDebouncing: boolean;
  /** Set a specific search value */
  setSearchValue: (field: string, value: string) => void;
  /** Set multiple search values at once */
  setSearchValues: (values: Record<string, string>) => void;
  /** Clear all search values */
  clearAllSearches: () => void;
  /** Clear a specific search value */
  clearSearch: (field: string) => void;
}

/**
 * Hook for multiple debounced search fields
 *
 * @example
 * ```tsx
 * const { searchValues, debouncedSearchValues, setSearchValue } = useMultiDebouncedSearch({
 *   fields: ['name', 'email', 'company'],
 *   initialValues: { name: '', email: '', company: '' }
 * });
 * ```
 */
export function useMultiDebouncedSearch(
  config: MultiDebouncedSearchConfig
): MultiDebouncedSearchState {
  const { delay = 300, initialValues = {}, fields } = config;

  // Initialize search values
  const initialSearchValues = fields.reduce(
    (acc, field) => {
      acc[field] = initialValues[field] || "";
      return acc;
    },
    {} as Record<string, string>
  );

  const [searchValues, setSearchValues] = useState(initialSearchValues);

  // Create debounced values for each field using the standardized debounce hook
  // We'll use a single debounce hook for the entire search values object
  const debouncedSearchValues = useDebounce(searchValues, delay);

  // Check if any field is debouncing
  const isDebouncing = fields.some((field) => searchValues[field] !== debouncedSearchValues[field]);

  const setSearchValue = useCallback((field: string, value: string) => {
    setSearchValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setMultipleSearchValues = useCallback((values: Record<string, string>) => {
    setSearchValues((prev) => ({ ...prev, ...values }));
  }, []);

  const clearAllSearches = useCallback(() => {
    const clearedValues = fields.reduce(
      (acc, field) => {
        acc[field] = "";
        return acc;
      },
      {} as Record<string, string>
    );
    setSearchValues(clearedValues);
  }, [fields]);

  const clearSearch = useCallback(
    (field: string) => {
      setSearchValue(field, "");
    },
    [setSearchValue]
  );

  return {
    searchValues,
    debouncedSearchValues,
    isDebouncing,
    setSearchValue,
    setSearchValues: setMultipleSearchValues,
    clearAllSearches,
    clearSearch,
  };
}
